import React, { useState, useEffect } from 'react';
import SelectField from '../common/SelectField.js';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../common/Header.js';
import Sidebar from '../common/Sidebar.js';
import Alert from '../common/Alert.js';
import inventoryUsageService from '../../services/InventoryUsageService.js';
import inventoryService from '../../services/InventoryService.js';
import userService from '../../services/UserService.js';
import axiosInstance from '../../config/axios.js';
import { FileText } from "lucide-react";
import { useTranslation } from 'react-i18next';


const AddInventoryUsage = () => {
  const { t } = useTranslation();
  const { inventoryId } = useParams();
  const [formData, setFormData] = useState({
    inventory_id: inventoryId || '',
    usage_type: '',
    usage_purpose: '',
    quantity_used: '',
    used_by_user_id: '',
    used_by_name: '',
    department: '',
    vehicle_id: '',
    usage_date: new Date().toISOString().split('T')[0],
    description: ''
  });
  
  const [returnReceipt, setReturnReceipt] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [selectedInventory, setSelectedInventory] = useState(null);
  const navigate = useNavigate();

  const usageTypes = inventoryUsageService.getUsageTypes();
  const usagePurposes = inventoryUsageService.getUsagePurposes();

  useEffect(() => {
    fetchInventoryItems();
    fetchUsers();
    fetchVehicles();
    if (inventoryId) {
      fetchInventoryItem(inventoryId);
    }
  }, [inventoryId]);

  const fetchInventoryItems = async () => {
    try {
      const response = await axiosInstance.get('/inventory?all=true');
      setInventoryItems(response.data.data || []);
    } catch (err) {
      console.error('Failed to load inventory items', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/users?all=true');
      setUsers(response.data.data || []);
    } catch (err) {
      console.error('Failed to load users', err);
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await axiosInstance.get('/vehicles?all=true');
      setVehicles(response.data.data || []);
    } catch (err) {
      console.error('Failed to load vehicles', err);
    }
  };

  const fetchInventoryItem = async (id) => {
    try {
      const response = await inventoryService.getById(id);
      setSelectedInventory(response.data);
    } catch (err) {
      console.error('Failed to load inventory item', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === 'inventory_id') {
      fetchInventoryItem(value);
    }

    if (name === 'used_by_user_id' && value) {
      const user = users.find(u => u.user_id === value);
      if (user) {
        setFormData(prev => ({ ...prev, used_by_name: user.name }));
      }
    }
  };

  const handleReceiptChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Alert.error('Receipt must be less than 5MB');
        return;
      }
      setReturnReceipt(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setReceiptPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeReceipt = () => {
    setReturnReceipt(null);
    setReceiptPreview(null);
    const fileInput = document.getElementById('receipt-input');
    if (fileInput) fileInput.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await inventoryUsageService.create(formData, returnReceipt);
      Alert.success('Inventory usage recorded successfully!');
      
      // Show inventory status
      if (response.inventory_status) {
        const { remaining_quantity, status, is_out_of_stock } = response.inventory_status;
        Alert.info(`Remaining stock: ${remaining_quantity} units. Status: ${status}`);
      }
      
      setTimeout(() => {
        if (inventoryId) {
          navigate(`/inventory/view/${inventoryId}`);
        } else {
          navigate('/inventory');
        }
      }, 2000);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to record inventory usage');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex bg-light w-full mt-16 sm:mt-0">
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-auto p-3 sm:p-6">
              <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-4">
                  <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                    <FileText className="w-5 h-5 text-buttonBg" /> Record Inventory Usage
                  </h1>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="bg-white rounded-sm shadow-sm p-4 sm:p-8 space-y-6 sm:space-y-8">
                  {/* Section 1: Item Selection */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Item Selection</h3>
                    <div className="grid grid-cols-1 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Inventory Item <span className="text-danger font-bold">*</span>
                        </label>
                        <SelectField variant="form"
                          name="inventory_id"
                          value={formData.inventory_id}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          required
                          disabled={!!inventoryId}
                        >
                          <option value="">Select Inventory Item</option>
                          {inventoryItems.map((item) => (
                            <option key={item.inventory_id} value={item.inventory_id}>
                              {item.item_name} - {item.inventory_id} (Available: {item.quantity} {item.unit})
                            </option>
                          ))}
                        </SelectField>
                      </div>

                      {selectedInventory && (
                        <div className="bg-blue-50 p-4 rounded-md">
                          <h4 className="font-semibold text-blue-900 mb-2">Selected Item Details</h4>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                            <div>
                              <span className="text-blue-700">Category:</span>
                              <p className="text-blue-900 font-medium">{selectedInventory.category}</p>
                            </div>
                            <div>
                              <span className="text-blue-700">Available:</span>
                              <p className="text-blue-900 font-medium">{selectedInventory.quantity} {selectedInventory.unit}</p>
                            </div>
                            <div>
                              <span className="text-blue-700">Unit Price:</span>
                              <p className="text-blue-900 font-medium">₹{selectedInventory.unit_price}</p>
                            </div>
                            <div>
                              <span className="text-blue-700">Location:</span>
                              <p className="text-blue-900 font-medium">{selectedInventory.warehouse_location}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Section 2: Usage Details */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Usage Details</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Usage Type <span className="text-danger font-bold">*</span>
                        </label>
                        <SelectField variant="form"
                          name="usage_type"
                          value={formData.usage_type}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          required
                        >
                          <option value="">Select Usage Type</option>
                          {usageTypes.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </SelectField>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Usage Purpose <span className="text-danger font-bold">*</span>
                        </label>
                        <SelectField variant="form"
                          name="usage_purpose"
                          value={formData.usage_purpose}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          required
                        >
                          <option value="">Select Purpose</option>
                          {usagePurposes.map((purpose) => (
                            <option key={purpose.value} value={purpose.value}>
                              {purpose.label}
                            </option>
                          ))}
                        </SelectField>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Quantity Used <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="number"
                          name="quantity_used"
                          value={formData.quantity_used}
                          onChange={handleChange}
                          placeholder="Enter quantity"
                          min="1"
                          max={selectedInventory?.quantity || ''}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Usage Date <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="date"
                          name="usage_date"
                          value={formData.usage_date}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          required
                        />
                      </div>
                    </div>

                    {formData.quantity_used && selectedInventory && (
                      <div className="mt-4 bg-green-50 p-3 rounded-md">
                        <p className="text-sm text-green-800">
                          <span className="font-semibold">Estimated Cost:</span> ₹
                          {(formData.quantity_used * selectedInventory.unit_price).toFixed(2)}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Section 3: User & Vehicle Information */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">User & Vehicle Information</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Used By (User)
                        </label>
                        <SelectField variant="form"
                          name="used_by_user_id"
                          value={formData.used_by_user_id}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        >
                          <option value="">Select User (Optional)</option>
                          {users.map((user) => (
                            <option key={user.user_id} value={user.user_id}>
                              {user.name} - {user.user_id}
                            </option>
                          ))}
                        </SelectField>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Used By Name <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="text"
                          name="used_by_name"
                          value={formData.used_by_name}
                          onChange={handleChange}
                          placeholder="Enter name"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Department
                        </label>
                        <input
                          type="text"
                          name="department"
                          value={formData.department}
                          onChange={handleChange}
                          placeholder="Enter department"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Vehicle (Optional)
                        </label>
                        <SelectField variant="form"
                          name="vehicle_id"
                          value={formData.vehicle_id}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        >
                          <option value="">Select Vehicle (Optional)</option>
                          {vehicles.map((vehicle) => (
                            <option key={vehicle.vehicle_number} value={vehicle.vehicle_number}>
                              {vehicle.vehicle_number} - {vehicle.vehicle_name}
                            </option>
                          ))}
                        </SelectField>
                      </div>
                    </div>
                  </div>

                  {/* Section 4: Description & Receipt */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Description & Receipt</h3>
                    <div className="grid grid-cols-1 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Description
                        </label>
                        <textarea
                          name="description"
                          value={formData.description}
                          onChange={handleChange}
                          placeholder="Enter usage description"
                          rows="3"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Return Receipt / Proof
                        </label>
                        <input
                          id="receipt-input"
                          type="file"
                          accept="image/*,.pdf"
                          onChange={handleReceiptChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                        <p className="text-xs text-gray-600 mt-1">Upload receipt or proof (PDF or Image, Max 5MB)</p>
                        {receiptPreview && (
                          <div className="mt-3">
                            <div className="relative inline-block">
                              <img 
                                src={receiptPreview} 
                                alt="Receipt Preview" 
                                className="w-32 h-32 sm:w-40 sm:h-40 object-cover border-2 border-gray-300 rounded-md"
                              />
                              <button
                                type="button"
                                onClick={removeReceipt}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors text-sm"
                                title="Remove receipt"
                              >
                                ×
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                    <button
                      type="submit"
                      disabled={loading}
                      className="group flex items-center justify-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 text-xs sm:text-sm text-buttonTxt bg-buttonBg hover:text-buttonBg hover:bg-buttonTxt border 
border-current rounded-sm  transition-all duration-300 font-medium whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Recording...' : 'Record Usage'}
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate(inventoryId ? `/inventory/view/${inventoryId}` : '/inventory')}
                      className="group flex items-center justify-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 text-xs sm:text-sm text-buttonBg bg-buttonTxt hover:text-buttonTxt 
hover:bg-buttonBg border border-current rounded-sm  transition-all duration-300 font-medium whitespace-nowrap"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddInventoryUsage;